/**
 * User tier — the ONE place that answers "what tier is this person?"
 * (tiered-exchange Plan 1 — see docs/blocks/tiered-exchange-epic.md
 * § Tiers × Auth; the one-record rule: docs/core/auth.md § Account Tiers).
 *
 *   Anon   — no profile keys. Receive & save contacts only.
 *   Simple — a profile minted with "Get your QR" (no login). Own QR, People
 *            page, groups, location, photos. NO chat/Posts/Nearby/push/5-tab nav.
 *   Full   — a profile that came through pending + a phone code.
 *
 * The record is the database (`profiles.account_status`, written only by the
 * server — migration 038). The phone keeps a copy in `account_tier`, written
 * at the same transitions: the Simple mint writes 'simple', a login or
 * conversion writes 'full' from the server's answer. Every profile boot
 * reconciles the copy against the database (reconcileTier, below).
 *
 * The tier no longer reads the auth session: an expired Full login boots on a
 * fresh anonymous session and must still read Full. The session reading
 * survives only as the one-session fallback for an install that has no copy
 * yet and couldn't read the database (DemoGate logs `tier_seed_fallback`).
 *
 * Call sites ask (isFullTier / getUserTier / isProfileWorld) — they never
 * re-derive tier from raw localStorage keys.
 */

import { hasDownstreamLadder } from './exchangeVariant';

export const TIER_ANON = 'anon';
export const TIER_SIMPLE = 'simple';
export const TIER_FULL = 'full';

/**
 * The database status of a Simple profile merged into the account on its
 * number (migration 039, simple-merge): retired — its connections live on the
 * account now. Never a tier; a device holding this id shows the takeover.
 */
export const ACCOUNT_STATUS_MERGED = 'merged';

/** True when the database row is a retired (merged) Simple profile. */
export const isMergedAccountStatus = (accountStatus) => accountStatus === ACCOUNT_STATUS_MERGED;

/** localStorage key holding the phone's copy of the tier ('simple' | 'full'). */
export const ACCOUNT_TIER_KEY = 'account_tier';

/**
 * Forget the copy — only when the device switches to a different profile id
 * (the dev profile-id editor): the next open seeds it from that profile's
 * row, so a stale 'simple' can never read as "converted elsewhere".
 */
export const clearStoredTier = () => localStorage.removeItem(ACCOUNT_TIER_KEY);

/** The database value (`profiles.account_status`) → the phone's tier. */
export const tierFromAccountStatus = (accountStatus) => {
  if (accountStatus === 'full') return TIER_FULL;
  if (accountStatus === 'anon') return TIER_SIMPLE;
  return null;
};

/** The stored copy, or null when this install has none (or it is malformed). */
export const getStoredTier = () => {
  const v = localStorage.getItem(ACCOUNT_TIER_KEY);
  return v === TIER_SIMPLE || v === TIER_FULL ? v : null;
};

/** Write the copy — only at a transition or a reconcile against the database. */
export const setStoredTier = (tier) => {
  if (tier !== TIER_SIMPLE && tier !== TIER_FULL) return;
  localStorage.setItem(ACCOUNT_TIER_KEY, tier);
};

/**
 * Is the current Supabase auth session anonymous? Reads the sb-*-auth-token
 * cache synchronously. Not a tier signal (see header) — used by the profile
 * sync's owner stamp and the keyless-install fallback only. Returns true / false, or null when unreadable (no
 * token yet, malformed JSON).
 */
// Raw-string-keyed memo: the token is re-read every call (no staleness), only
// the key-name scan and multi-KB JSON parse are skipped when unchanged — this
// predicate now sits on per-message hot paths (getSenderId, canonicalizeSenderId).
let cachedTokenKey = null;
let lastTokenRaw;
let lastTokenVerdict = null;
export const isAnonymousAuthSession = () => {
  try {
    if (!cachedTokenKey || !(cachedTokenKey in localStorage)) {
      cachedTokenKey =
        Object.keys(localStorage).find((k) => k.startsWith('sb-') && k.endsWith('-auth-token')) ||
        null;
      if (!cachedTokenKey) return null;
    }
    const raw = localStorage.getItem(cachedTokenKey);
    if (raw !== lastTokenRaw) {
      lastTokenRaw = raw;
      const isAnon = JSON.parse(raw)?.user?.is_anonymous;
      lastTokenVerdict = typeof isAnon === 'boolean' ? isAnon : null;
    }
    return lastTokenVerdict;
  } catch {
    return null;
  }
};

/**
 * The current tier. Synchronous — profile keys decide Anon (both required; a
 * lone profile_user_id from an aborted conversion is stale), the stored copy
 * decides Simple vs Full.
 *
 * @returns {'anon'|'simple'|'full'}
 */
export const getUserTier = () => {
  const profileUserId = localStorage.getItem('profile_user_id');
  const profileDataRaw = localStorage.getItem('profile_user_data');
  if (!profileUserId || !profileDataRaw) return TIER_ANON;

  const stored = getStoredTier();
  if (stored) return stored;

  // No copy yet and DemoGate's one read failed (logged there): the old session
  // reading, for this session only — never stored. Unknown reads Full (never
  // demote an existing user on a missing token).
  return isAnonymousAuthSession() === true ? TIER_SIMPLE : TIER_FULL;
};

/**
 * What a profile boot does with the database's answer. Pure — unit-tested.
 *
 *   'none'     — agree, no answer, or no copy (DemoGate seeds a keyless install)
 *   'takeover' — the copy says Simple, the database says Full, and the profile
 *                is owned by ANOTHER session: converted on another device. The
 *                old container shows "Your account is now active in the app".
 *                Needs a stored copy and a known owner on both sides — the
 *                takeover clears local data, so a false hit must be impossible.
 *   'promote'  — Simple → Full on THIS session (converted here, died before
 *                writing the copy): write 'full'.
 *   'demote'   — the copy says Full, the database says Simple: write 'simple'.
 *
 * A merged row (039) is a takeover whatever the copy says — the server retired
 * this exact profile id, and only Simple rows are ever merged, so the device
 * holding it is the retired profile's own (no false hit possible).
 *
 * @param {{ stored: string|null, accountStatus: string|null|undefined,
 *           ownerAuthId: string|null|undefined, sessionAuthId: string|null|undefined }} args
 * @returns {'none'|'takeover'|'promote'|'demote'}
 */
export const reconcileTier = ({ stored, accountStatus, ownerAuthId, sessionAuthId }) => {
  if (isMergedAccountStatus(accountStatus)) return 'takeover';
  const dbTier = tierFromAccountStatus(accountStatus);
  if (!stored || !dbTier || stored === dbTier) return 'none';
  if (stored === TIER_FULL) return 'demote';
  // stored Simple, database Full
  if (!ownerAuthId || !sessionAuthId) return 'none';
  return ownerAuthId === sessionAuthId ? 'promote' : 'takeover';
};

/**
 * reconcileTier for this device against its own profiles row
 * ({ account_status, auth_user_id }) — the one call both boot readers make
 * (ensureProfileSynced, useConversionCheck), so the takeover rule can't drift.
 */
export const reconcileTierWithRow = (row, sessionAuthId) =>
  reconcileTier({
    stored: getStoredTier(),
    accountStatus: row?.account_status,
    ownerAuthId: row?.auth_user_id,
    sessionAuthId,
  });

/** Full-account gate — chat, Posts, Nearby, push, PXP, 5-tab nav, device sessions. */
export const isFullTier = () => getUserTier() === TIER_FULL;

/**
 * Simple-tier gate — Simple profiles only exist under the variants that carry
 * the downstream ladder (CARD/TEXT since plan 1, THREAD since plan 3 D3 — the
 * mint door lives behind them), so the compound lives HERE, once: four call
 * sites were hand-composing the flag-and-tier check (review 2026-08-09) — the
 * exact re-derivation drift this module exists to retire.
 *
 * NOTE (plan 3 D1a): this goes live in PRODUCTION at the default flip. Under
 * CHAT it is always false, so no prod tester has ever reached the mint door;
 * once main resolves THREAD, real testers' Anon counterparts can mint their
 * own QR and connect onward. That is the milestone's whole point, but it is a
 * deliberate decision, not a side effect of a screen swap.
 */
export const isSimpleTier = () => hasDownstreamLadder() && getUserTier() === TIER_SIMPLE;

/** Profile-world gate — Simple and Full both live on the profile-* surfaces (D5). */
export const isProfileWorld = () => getUserTier() !== TIER_ANON;

/**
 * Screens only a Full account may open. A Simple profile navigating here is
 * redirected to People by the nav guard (NavigationContext).
 */
export const FULL_ONLY_SCREENS = new Set(['profile-posts', 'profile-chat-list']);
